import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  CompleteBottomSheetTakeOverInput,
  Mutation,
  PinwheelExperienceInput,
} from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import React, { RefObject, useContext } from 'react';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line no-restricted-imports
import { StyleSheet, Text, View } from 'react-native';
import CompleteBottomSheetTakeOver from 'services/graphql/mutations/onboarding/CompleteBottomSheetTakeOver.mutation';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import Illustration from 'shared/components/vxl/Illustration/Illustration';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import HyperlinkXsText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkXSText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import useDirectDepositLoadingComponent from 'shared/hooks/pinwheel/useDirectDepositLoadingComponent/useDirectDepositLoadingComponent';
import useDirectDepositManagementFlow from 'shared/hooks/pinwheel/useDirectDepositManagementFlow/useDirectDepositManagementFlow';
import { PinwheelContext } from 'shared/providers/pinwheel/PinwheelContextProvider/PinwheelContextProvider';
import theme from 'shared/styles/theme';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';
import { useMutation } from 'urql';

interface BottomSheetData {
  id: string;
  title: string;
  body: string;
  disclosure: string;
  cta: string;
}
interface OnboardingCheckListBottomSheetProps {
  bottomSheetData: BottomSheetData;
  isDDBottomSheetPresented: boolean;
  isDdSetUpDone: boolean;
  bottomSheetRef: RefObject<BottomSheetModal | null> | null;
  onDisclosurePress: () => void;
}

const OnboardingCheckListBottomSheet = ({
  bottomSheetData,
  isDDBottomSheetPresented,
  isDdSetUpDone,
  bottomSheetRef,
  onDisclosurePress,
}: OnboardingCheckListBottomSheetProps) => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('home', {
    keyPrefix: 'progressTracker',
  });
  const [, submitCompleteBottomSheetTakeOver] = useMutation<Mutation>(
    CompleteBottomSheetTakeOver,
  );

  const { skipIntroScreen } = useDirectDepositLoadingComponent();

  const {
    shouldRedirectToDDManagementPage,
    shouldLaunchPinwheelSDK,
    refetchDdManagementFlow,
  } = useDirectDepositManagementFlow({
    pause: false,
  });

  useViewDidFocusScreenAnalyticsEvent(
    isDDBottomSheetPresented
      ? {
          key: '/onboardingChecklistCompleteNotification',
        }
      : null,
  );

  const disclosureClickAnalytics = usePressableAnalytics({
    analyticsId: 'onboardingDisclosuresAccessed',
  });

  const initiateDDClickAnalytics = usePressableAnalytics({
    analyticsId: 'directDepositFlowInitiated',
  });

  const bottomSheetDoneAnalytics = usePressableAnalytics({
    analyticsId: 'onboardingChecklistComplete',
  });

  const { initiatePinwheel } = useContext(PinwheelContext);

  const onDisclosureHyperlinkClicked = () => {
    disclosureClickAnalytics();
    onDisclosurePress?.();
  };

  const handleCompleteBottomSheetTakeOver = async (): Promise<void> => {
    try {
      await submitCompleteBottomSheetTakeOver(
        {
          input: {
            takeoverId: bottomSheetData?.id,
          } as CompleteBottomSheetTakeOverInput,
        },
        { fetchPolicy: 'network-only' },
      );
    } catch (error) {
      if (!(error instanceof Error && error.name === 'AbortError')) {
        console.error('Failed to complete bottom sheet takeover:', error);
      }
    }
  };

  const setUpDDorDoneClicked = async () => {
    try {
      await handleCompleteBottomSheetTakeOver();
      if (!isDdSetUpDone) {
        if (shouldLaunchPinwheelSDK && !shouldRedirectToDDManagementPage) {
          initiatePinwheel({
            input: {
              experience: PinwheelExperienceInput.DirectDepositSwitch,
              skipIntroScreen,
            },
            launchBehavior: skipIntroScreen
              ? { delayAfterTokenMs: 3000 }
              : undefined,
            onOutcome: refetchDdManagementFlow,
          });
        } else {
          router.push('/move-money/direct-deposit/direct-deposit-management');
        }
        initiateDDClickAnalytics();

        bottomSheetRef?.current?.dismiss();
      } else {
        bottomSheetRef?.current?.dismiss();
        bottomSheetDoneAnalytics();
      }
    } catch (error) {
      logErrorWithContext(
        'Error in dismissing onboarding bottomsheet: ' + error,
        {
          featureArea: FeatureArea.ONBOARDING_CHECKLIST,
          responsibleTeam: ResponsibleTeam.CUSTOMER_ACQUISITION,
          severity: ErrorSeverity.LOW,
        },
      );
    }
  };

  return (
    <BottomSheet
      ref={bottomSheetRef}
      onDismiss={handleCompleteBottomSheetTakeOver}
    >
      <View style={styles.bottomSheet} testID="ddsetup-bottom-sheet">
        <View style={styles.visualContainer}>
          <Illustration illustration="DISCOBALL_A" />
        </View>
        <View style={styles.title}>
          <TitleLgText numberOfLines={0}>{bottomSheetData?.title}</TitleLgText>
        </View>
        <View style={styles.bottomSheetDescription}>
          <BodyBaseText
            key="description"
            style={styles.bottomSheetDescriptionText}
          >
            {bottomSheetData?.body}
          </BodyBaseText>
        </View>
        <View style={styles.disclosureLink}>
          <BodyXSText style={styles.qualsApplyText}>
            <Text>{translatedText('qualsApply')}</Text>
            <HyperlinkXsText onPress={onDisclosureHyperlinkClicked}>
              {translatedText('disclosure')}
            </HyperlinkXsText>
          </BodyXSText>
        </View>

        <View style={styles.doneButton}>
          <Button
            label={
              isDdSetUpDone ? translatedText('done') : bottomSheetData?.cta
            }
            onPress={setUpDDorDoneClicked}
            size="LARGE"
            type="PRIMARY"
            width="FULL"
          />
        </View>
      </View>
    </BottomSheet>
  );
};

const styles = StyleSheet.create({
  bottomSheet: {
    paddingBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
    paddingTop: 0,
  },
  bottomSheetDescription: {
    alignItems: 'center',
    paddingBottom: theme.spacing.m,
    paddingTop: theme.spacing.m,
    paddingHorizontal: theme.spacing.l,
  },
  bottomSheetDescriptionText: {
    textAlign: 'center',
  },
  disclosureLink: {
    alignItems: 'center',
  },
  doneButton: {
    paddingTop: theme.spacing.xl,
    paddingBottom: theme.spacing.m,
  },
  qualsApplyText: {
    color: theme.color.text.secondary,
  },
  title: {
    alignItems: 'center',
  },
  visualContainer: {
    alignItems: 'center',
    alignSelf: 'center',
    height: 88,
    paddingHorizontal: theme.spacing.m,
    paddingBottom: theme.spacing.m,
    width: 104,
  },
});

export default OnboardingCheckListBottomSheet;
